import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const ConveyancingDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/conveyancing/dashboard/', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                if (response.status === 401 || response.status === 403) throw new Error('auth');
                throw new Error('network');
            }
            const data = await response.json();
            setDashboardData(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const statusColors = {
        'Instruction Received': 'bg-blue-100 text-blue-700',
        'Title Searches': 'bg-purple-100 text-purple-700',
        'Document Drafting': 'bg-indigo-100 text-indigo-700',
        'Signing': 'bg-yellow-100 text-yellow-700',
        'Lodgement': 'bg-orange-100 text-orange-700',
        'Registration': 'bg-cyan-100 text-cyan-700',
        'Completed': 'bg-green-100 text-green-700',
        'Cancelled': 'bg-red-100 text-red-700',
        'On Hold': 'bg-gray-100 text-gray-700',
    };

    if (loading) {
        return (
            <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
                <div className="mb-8">
                    <div className="h-8 w-72 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2"></div>
                    <div className="h-4 w-96 bg-gray-100 dark:bg-gray-800 rounded animate-pulse"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
                            <div className="h-3 w-20 bg-gray-100 dark:bg-gray-700 rounded animate-pulse mb-2"></div>
                            <div className="h-8 w-16 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Conveyancing Management</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Property transfers and trust account management</p>
                </div>
                <div className="max-w-lg mx-auto mt-12">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
                        {error === 'auth' ? (
                            <>
                                <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                                    </svg>
                                </div>
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Session Expired</h2>
                                <p className="text-gray-500 dark:text-gray-400 mb-6">Please sign in again.</p>
                                <Link to="/login" className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Sign In</Link>
                            </>
                        ) : (
                            <>
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Welcome to Conveyancing</h2>
                                <p className="text-gray-500 dark:text-gray-400 mb-6">Start managing your property transfers with integrated trust account handling.</p>
                                <Link to="/conveyancing/matters/new" className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Create First Matter
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const data = dashboardData;

    return (
        <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
            {/* Header */}
            <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Conveyancing Management</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Property transfers and trust account management</p>
                </div>
                <Link
                    to="/conveyancing/matters/new"
                    className="mt-4 sm:mt-0 inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    New Matter
                </Link>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center">
                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Active Matters</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{data.active_matters}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center">
                        <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Completed</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{data.completed_matters}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center">
                        <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Value</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(data.total_value)}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center">
                        <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Overdue Tasks</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{data.overdue_milestones}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Links */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Link to="/conveyancing/matters" className="flex items-center p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md transition-all group">
                    <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center mr-3 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/40 transition-colors">
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
                        </svg>
                    </div>
                    <div>
                        <p className="font-semibold text-gray-900 dark:text-white">All Matters</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{data.total_matters} total matters</p>
                    </div>
                </Link>

                <Link to="/trust" className="flex items-center p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-600 hover:shadow-md transition-all group">
                    <div className="w-10 h-10 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-center mr-3 group-hover:bg-green-100 dark:group-hover:bg-green-900/40 transition-colors">
                        <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
                        </svg>
                    </div>
                    <div>
                        <p className="font-semibold text-gray-900 dark:text-white">Trust Accounts</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Manage trust funds</p>
                    </div>
                </Link>

                <Link to="/conveyancing/matters/new" className="flex items-center p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600 hover:shadow-md transition-all group">
                    <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg flex items-center justify-center mr-3 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900/40 transition-colors">
                        <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                    </div>
                    <div>
                        <p className="font-semibold text-gray-900 dark:text-white">New Matter</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Start property transfer</p>
                    </div>
                </Link>
            </div>

            {/* Status Breakdown & Recent Matters */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Status Breakdown */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Status Overview</h2>
                    {Object.keys(data.status_breakdown).length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400 text-sm">No matters yet</p>
                    ) : (
                        <div className="space-y-3">
                            {Object.entries(data.status_breakdown).map(([label, count]) => (
                                <div key={label} className="flex items-center justify-between">
                                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusColors[label] || 'bg-gray-100 text-gray-700'}`}>
                                        {label}
                                    </span>
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{count}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {data.pending_registrations > 0 && (
                        <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                            <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">
                                {data.pending_registrations} matter(s) pending registration
                            </p>
                        </div>
                    )}
                </div>

                {/* Recent Matters */}
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Matters</h2>
                        <Link to="/conveyancing/matters" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">View all</Link>
                    </div>
                    {data.recent_matters.length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400 text-sm">No matters to display</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-100 dark:border-gray-700">
                                        <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Reference</th>
                                        <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Property</th>
                                        <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Client</th>
                                        <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                                        <th className="text-right py-3 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Price</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.recent_matters.map(matter => (
                                        <tr key={matter.id} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                                            <td className="py-3 px-2">
                                                <Link to={`/conveyancing/matters/${matter.id}`} className="text-blue-600 dark:text-blue-400 hover:underline font-medium text-sm">
                                                    {matter.reference_number}
                                                </Link>
                                            </td>
                                            <td className="py-3 px-2 text-sm text-gray-700 dark:text-gray-300 max-w-[200px] truncate">{matter.property_address}</td>
                                            <td className="py-3 px-2 text-sm text-gray-700 dark:text-gray-300">{matter.client_name}</td>
                                            <td className="py-3 px-2">
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[matter.status_display] || 'bg-gray-100 text-gray-700'}`}>
                                                    {matter.status_display}
                                                </span>
                                            </td>
                                            <td className="py-3 px-2 text-sm text-gray-900 dark:text-white font-medium text-right">
                                                {formatCurrency(matter.purchase_price, matter.currency)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConveyancingDashboard;
